import { useMemo } from 'react'
import { useTranslation } from '@/i18n'
import type { MockNode, MockNodeType } from './types'
import { NODE_TYPE_STYLES } from './nodeIcons'

interface Props {
  nodes: MockNode[]
  enabledTypes: Set<MockNodeType>
  onToggle: (type: MockNodeType) => void
  /**
   * Hide the per-type counts. The Tag mapping drill-down's legend is a pure key
   * in the design — full taxonomy, no numbers — while the Knowledge Graph page
   * keeps its counts. Off by default so that page is unchanged.
   */
  hideCounts?: boolean
  /** Heading of the first section. Defaults to "Types"; the Knowledge Graph page's Figma says "Entity". */
  title?: string
  /**
   * While the toolbar search is active: the types the search keeps (matches
   * and their ancestors). Only those rows are drawn, and a section with none
   * of them is left out, so the legend lists exactly what is on screen (the
   * design's search frame). `null`/omitted means no search: every row.
   */
  presentTypes?: Set<MockNodeType> | null
}

interface TypeMeta {
  type: MockNodeType
  label: string
}

/** The node's disc, ring and icon at legend size (24px disc, 16px icon, per the legend design), so the key reads as the canvas does. */
const TypeIcon = ({ type }: { type: MockNodeType }) => {
  const style = NODE_TYPE_STYLES[type]
  return (
    <span
      data-testid={`legend-icon-${type}`}
      className="grid size-6 shrink-0 place-items-center rounded-full border"
      style={{ backgroundColor: style.bg, borderColor: style.ring }}
    >
      <svg
        viewBox="0 0 24 24"
        width={16}
        height={16}
        fill="none"
        stroke={style.fg}
        // Filled (Energy Icons) markup paints with currentColor.
        color={style.fg}
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        // Static icon markup from nodeIcons, never user data.
        dangerouslySetInnerHTML={{ __html: style.iconPaths }}
      />
    </span>
  )
}

const TYPES_SECTION: TypeMeta[] = [
  { type: 'enterprise', label: 'Enterprise' },
  { type: 'site', label: 'Site' },
  { type: 'operating_area', label: 'Operating Area' },
  { type: 'operation_unit', label: 'Operating Unit' },
  { type: 'system', label: 'System' },
  { type: 'functional_location', label: 'Functional Location' },
  { type: 'components', label: 'Components' },
]

const DOCUMENTS_SECTION: TypeMeta[] = [
  { type: 'documents', label: 'Documents' },
]

const TypesPanel = ({ nodes, enabledTypes, onToggle, hideCounts, title, presentTypes }: Props) => {
  const { t } = useTranslation()

  const counts = useMemo(() => {
    const map = new Map<MockNodeType, number>()
    nodes.forEach((node) => map.set(node.type, (map.get(node.type) ?? 0) + 1))
    return map
  }, [nodes])

  const renderItem = (meta: TypeMeta) => {
    const enabled = enabledTypes.size === 0 || enabledTypes.has(meta.type)
    const count = counts.get(meta.type) ?? 0
    return (
      <button
        key={meta.type}
        type="button"
        onClick={() => onToggle(meta.type)}
        className={`flex items-center gap-2 w-full rounded-md pl-2 pr-0 py-[3px] text-left text-xs leading-4 hover:bg-gray-50 dark:hover:bg-dark-muted transition-colors ${
          enabled ? 'text-foreground' : 'text-muted-foreground/60'
        }`}
      >
        <span className={enabled ? '' : 'opacity-40'}>
          <TypeIcon type={meta.type} />
        </span>
        <span className="flex-1 truncate">{meta.label}</span>
        {!hideCounts && <span className="text-[10px] text-muted-foreground">{count}</span>}
      </button>
    )
  }

  /* The design's legend is a plain key: each section is a heading and its rows,
     at the same size as the rows with only the weight setting it apart. It does
     not collapse — there were chevrons, and nothing to gain by hiding seven
     rows — and the two sections run together with no rule between them.
     Each row leads with the type's node icon (a 20px disc) rather than a dot,
     so the key matches the canvas; rows sit on a 26px pitch. */
  const section = (heading: string, all: TypeMeta[]) => {
    const items = presentTypes ? all.filter((m) => presentTypes.has(m.type)) : all
    if (items.length === 0) return null
    return (
      <div>
        <div className="px-4 pt-0.5 pb-[7px] text-xs font-semibold text-foreground">{heading}</div>
        <div className="flex flex-col pl-2 pr-1 pb-2">{items.map(renderItem)}</div>
      </div>
    )
  }

  return (
    <div className="absolute left-3 top-3 z-10 w-[180px] rounded-xl border border-border bg-background pt-4 pb-3 shadow-md dark:bg-dark-card">
      {section(title ?? String(t('knowledge_graph:types.title', { defaultValue: 'Types' })), TYPES_SECTION)}
      {section(String(t('knowledge_graph:documents.title', { defaultValue: 'Documents' })), DOCUMENTS_SECTION)}
    </div>
  )
}

export default TypesPanel
